import { useMemo, useState } from 'react';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { BoardTopBarActions, NewTaskCtaButton, type BoardFilter } from '@/components/BoardTopBarActions';
import { useParams } from '@tanstack/react-router';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import {
  useCreateTask,
  useProject,
  useReorderTask,
  useTags,
  useTasks,
  useWorkspace,
  useWorkspaceMembers,
  useWorkspaceStatuses,
} from '@fluxter/shared';
import {
  BoardSwimlane,
  BoardSwimlaneDragOverlay,
  type SwimlaneCategory,
} from '@/components/BoardSwimlane';
import { ProjectPageHeader } from '@/components/ProjectPageHeader';
import { ProjectViewSwitcher } from '@/components/ProjectViewSwitcher';
import type { TagLookup, TaskCardData } from '@/components/TaskCard';
import type { Priority } from '@/components/glass';

interface TaskListAttrs {
  title?: string;
  code?: string;
  status_id?: string;
  assignee_id?: string | null;
  due_at?: string | null;
  position?: number;
  parent_task_id?: string | null;
  tag_ids?: string[];
  subtask_count?: number;
  completed_at?: string | null;
  /** Glass redesign — Phase 1 backend addition. */
  priority?: Priority;
  /** Glass redesign — Phase 1 backend addition. Scribe inferred this
   *  as `string | null` because the example was null; the API actually
   *  returns an int. Cast at the consumer to recover the right type. */
  estimate_minutes?: string | number | null;
}

interface TaskListItem {
  id: string;
  attributes?: TaskListAttrs;
}

interface StatusAttrs {
  name?: string;
  color?: string;
  category?: SwimlaneCategory;
  is_default?: boolean;
  position?: number;
}

interface MemberAttrs {
  name?: string;
  email?: string;
}

interface TagAttrs {
  name?: string;
  color?: string;
}

const CATEGORY_LABEL: Record<SwimlaneCategory, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  complete: 'Complete',
};
const CATEGORY_ORDER: SwimlaneCategory[] = ['todo', 'in_progress', 'complete'];

/**
 * dnd-kit drop animation — slight scale-down + fade so the card "settles"
 * into place rather than snapping. Tuned to feel quick but not choppy.
 */
const dropAnimation: DropAnimation = {
  duration: 200,
  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: { opacity: '0.4' },
    },
  }),
};

export const BoardPage = () => {
  const { workspaceSlug, projectSlug } = useParams({
    from: '/w/$workspaceSlug/p/$projectSlug/board',
  });

  const workspace = useWorkspace(workspaceSlug);
  const project = useProject(workspaceSlug, projectSlug);
  const statuses = useWorkspaceStatuses(workspaceSlug);
  const members = useWorkspaceMembers(workspaceSlug);
  const tags = useTags(workspaceSlug);
  const tasks = useTasks(workspaceSlug, projectSlug);
  const createTask = useCreateTask(workspaceSlug, projectSlug);

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  // Composer state lifted into the page so the topbar's "New task"
  // CTA can open the To Do swimlane composer (matches prototype).
  const [composingCategory, setComposingCategory] = useState<SwimlaneCategory | null>(null);
  // Filter state — assignees + tags. Applied to topLevelTasks below.
  const [filter, setFilter] = useState<BoardFilter>({ assigneeIds: [], tagIds: [] });

  // Lookups -------------------------------------------------------------
  const projectName = (project.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;
  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const statusList =
    (statuses.data as { data?: Array<{ id: string; attributes?: StatusAttrs }> } | undefined)
      ?.data ?? [];
  const statusById = useMemo(
    () => new Map(statusList.map((s) => [s.id, s.attributes ?? {}])),
    [statusList],
  );

  const memberList =
    (members.data as { data?: Array<{ id: string; attributes?: MemberAttrs }> } | undefined)
      ?.data ?? [];
  const memberById = useMemo(
    () => new Map(memberList.map((m) => [m.id, m.attributes ?? {}])),
    [memberList],
  );

  const tagList =
    (tags.data as { data?: Array<{ id: string; attributes?: TagAttrs }> } | undefined)?.data ?? [];
  const tagLookup: TagLookup = useMemo(() => {
    const map = new Map(
      tagList.map((t) => [t.id, { name: t.attributes?.name ?? 'tag', color: t.attributes?.color ?? '#64748b' }]),
    );
    return { get: (id: string) => map.get(id) };
  }, [tagList]);

  const taskList = (tasks.data as { data?: TaskListItem[] } | undefined)?.data ?? [];

  // Top-level only on the board, sorted by absolute position, then
  // filtered by the topbar Filter popover. Subtasks contribute only
  // to the parent's `subtask_count` badge so they're elided here.
  const topLevelTasks = useMemo(() => {
    let rows = taskList
      .filter((t) => (t.attributes?.parent_task_id ?? null) === null)
      .slice()
      .sort((a, b) => (a.attributes?.position ?? 0) - (b.attributes?.position ?? 0));

    if (filter.assigneeIds.length > 0) {
      rows = rows.filter((t) =>
        t.attributes?.assignee_id
          ? filter.assigneeIds.includes(t.attributes.assignee_id)
          : false,
      );
    }
    if (filter.tagIds.length > 0) {
      const wanted = new Set(filter.tagIds);
      rows = rows.filter((t) =>
        (t.attributes?.tag_ids ?? []).some((id) => wanted.has(id)),
      );
    }

    return rows;
  }, [taskList, filter]);

  // Translate task rows into card data for rendering.
  const cards = useMemo<TaskCardData[]>(
    () =>
      topLevelTasks.map((t) => {
        const status = t.attributes?.status_id ? statusById.get(t.attributes.status_id) : undefined;
        const assignee = t.attributes?.assignee_id
          ? memberById.get(t.attributes.assignee_id)
          : undefined;
        // Recover the int type from Scribe's `string | null` inference
        // — the API column is unsigned int, never serialized as string.
        const estimateRaw = t.attributes?.estimate_minutes;
        const estimateMinutes =
          estimateRaw === null || estimateRaw === undefined
            ? null
            : typeof estimateRaw === 'number'
              ? estimateRaw
              : Number.parseInt(estimateRaw, 10);
        return {
          id: t.id,
          title: t.attributes?.title ?? 'Untitled',
          code: t.attributes?.code ?? null,
          statusId: t.attributes?.status_id ?? null,
          statusColor: status?.color ?? '#94a3b8',
          statusName: status?.name ?? '—',
          isComplete: status?.category === 'complete',
          priority: t.attributes?.priority ?? 'medium',
          estimateMinutes: Number.isFinite(estimateMinutes) ? estimateMinutes : null,
          // Logged time isn't aggregated on the list endpoint yet —
          // a follow-up will add `withSum('timeEntries', ...)` to the
          // controllers; for now cards just show the estimate.
          loggedMinutes: null,
          assigneeId: t.attributes?.assignee_id ?? null,
          assigneeName: assignee?.name ?? null,
          assigneeEmail: assignee?.email ?? null,
          dueAt: t.attributes?.due_at ?? null,
          tagIds: t.attributes?.tag_ids ?? [],
          subtaskCount: t.attributes?.subtask_count ?? 0,
        };
      }),
    [topLevelTasks, statusById, memberById],
  );

  // Group cards into the 3 fixed swimlanes by status category.
  const cardsByCategory = useMemo(() => {
    const buckets: Record<SwimlaneCategory, TaskCardData[]> = {
      todo: [],
      in_progress: [],
      complete: [],
    };
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const taskRow = topLevelTasks[i];
      if (!card || !taskRow) continue;
      const statusId = taskRow.attributes?.status_id;
      const cat = statusId ? statusById.get(statusId)?.category : undefined;
      if (cat) buckets[cat].push(card);
    }
    return buckets;
  }, [cards, topLevelTasks, statusById]);

  const activeCard = activeTaskId ? cards.find((c) => c.id === activeTaskId) ?? null : null;

  // Mutations -----------------------------------------------------------
  // taskUlid travels in the mutate input (not the hook factory) — the
  // active task changes per drag and React doesn't guarantee a render
  // commit between dnd-kit's onDragStart and onDragEnd in the same gesture,
  // so a hook-bound id can be stale (empty) at mutate time.
  const reorder = useReorderTask(workspaceSlug);

  // Sensors -------------------------------------------------------------
  const sensors = useSensors(
    // Small activation distance so clicks (e.g. card title link) still work.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Default status per category — used when a drag crosses swimlanes.
  const defaultStatusByCategory = useMemo(() => {
    const out: Partial<Record<SwimlaneCategory, string>> = {};
    for (const s of statusList) {
      const cat = s.attributes?.category;
      if (!cat) continue;
      // Take the first one with is_default; fall back to the first encountered.
      if (s.attributes?.is_default && !out[cat]) {
        out[cat] = s.id;
      }
    }
    // Backfill missing categories with the lowest-positioned status.
    for (const cat of CATEGORY_ORDER) {
      if (!out[cat]) {
        const first = statusList
          .filter((s) => s.attributes?.category === cat)
          .sort((a, b) => (a.attributes?.position ?? 0) - (b.attributes?.position ?? 0))[0];
        if (first) out[cat] = first.id;
      }
    }
    return out;
  }, [statusList]);

  // Drag handlers -------------------------------------------------------
  const handleDragStart = (event: DragStartEvent) => {
    setActiveTaskId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTaskId(null);
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const sourceTask = topLevelTasks.find((t) => t.id === activeId);
    if (!sourceTask) return;

    // Resolve destination swimlane.
    // Three cases:
    //  - over is another task → use that task's swimlane
    //  - over is a swimlane droppable → use it directly
    //  - else → no-op
    let destCategory: SwimlaneCategory | null = null;
    let destIndex = -1;

    if (overId.startsWith('swimlane:')) {
      destCategory = overId.slice('swimlane:'.length) as SwimlaneCategory;
      // Drop at end of the swimlane.
      destIndex = (cardsByCategory[destCategory] ?? []).length;
    } else {
      const overTask = topLevelTasks.find((t) => t.id === overId);
      if (!overTask) return;
      const overStatus = overTask.attributes?.status_id
        ? statusById.get(overTask.attributes.status_id)
        : undefined;
      destCategory = overStatus?.category ?? null;
      if (!destCategory) return;
      const lane = cardsByCategory[destCategory];
      destIndex = lane.findIndex((c) => c.id === overId);
      if (destIndex < 0) destIndex = lane.length;
    }

    if (!destCategory) return;

    const sourceStatus = sourceTask.attributes?.status_id
      ? statusById.get(sourceTask.attributes.status_id)
      : undefined;
    const sourceCategory = sourceStatus?.category ?? null;
    const isCrossCategory = sourceCategory !== destCategory;

    // Compute the absolute target position.
    //
    // The board groups by category but `position` is project-wide on
    // top-level tasks. Translate "drop at index K of dest lane" → absolute
    // position by looking up the lane card at K (after removing the source
    // if it was in the same lane) and using its absolute position. If K is
    // past the lane's last card, use that card's position + 1; if the lane
    // is empty, fall back to "end of project".
    const destLane = cardsByCategory[destCategory].filter((c) => c.id !== activeId);
    const positionOf = (cardId: string): number =>
      topLevelTasks.find((t) => t.id === cardId)?.attributes?.position ?? 0;

    let targetPosition: number;
    if (destLane.length === 0) {
      // Empty destination lane — drop at end of project.
      targetPosition = topLevelTasks.length + 1;
    } else if (destIndex >= destLane.length) {
      const last = destLane[destLane.length - 1];
      targetPosition = positionOf(last?.id ?? '') + 1;
    } else {
      const target = destLane[destIndex];
      targetPosition = positionOf(target?.id ?? '');
    }

    // No-op detection: same swimlane, same index, no status change.
    if (!isCrossCategory) {
      const currentLaneIndex = cardsByCategory[destCategory].findIndex((c) => c.id === activeId);
      if (currentLaneIndex === destIndex) return;
    }

    const newStatusId = isCrossCategory ? defaultStatusByCategory[destCategory] : undefined;

    // Fire the mutation. The hook handles optimistic cache updates +
    // rollback. We only surface the error toast here.
    reorder.mutate(
      { taskUlid: activeId, position: targetPosition, status_id: newStatusId },
      {
        onError: (err) => {
          const message =
            err instanceof Error ? err.message : 'Could not move card. Reverted.';
          toast.error(message);
        },
      },
    );
  };

  /**
   * Inline-composer create handler. Returns a Promise so the
   * swimlane composer can await before clearing its input;
   * surface any failure as a toast and re-throw so the composer
   * keeps the draft for retry.
   */
  const handleCreateInSwimlane = async (
    category: SwimlaneCategory,
    title: string,
  ): Promise<void> => {
    const statusId = defaultStatusByCategory[category];
    if (!statusId) return;
    try {
      await createTask.mutateAsync({ title, status_id: statusId });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create task.');
      throw err;
    }
  };

  // Total task count powers each column's progress bar — every column
  // shows its share of the total per the prototype.
  const totalTaskCount = cards.length;

  // Prototype topbar order: Search · Filter · Avatars · ViewSwitcher
  // · Theme · "New task". Theme is rendered by AppTopBar between the
  // `right` and `cta` slots; we put the CTA in `cta` so it lands AFTER
  // the theme toggle.
  const topBarRight = useMemo(
    () => (
      <>
        <BoardTopBarActions
          filter={filter}
          setFilter={setFilter}
          members={memberList.map((m) => ({
            id: m.id,
            name: m.attributes?.name ?? null,
            email: m.attributes?.email ?? null,
          }))}
          tags={tagList.map((t) => ({
            id: t.id,
            name: t.attributes?.name ?? 'tag',
            color: t.attributes?.color ?? '#64748b',
          }))}
        />
        <ProjectViewSwitcher
          workspaceSlug={workspaceSlug}
          projectSlug={projectSlug}
          current="board"
        />
      </>
    ),
    [filter, memberList, tagList, workspaceSlug, projectSlug],
  );
  const topBarCta = useMemo(
    () => <NewTaskCtaButton onClick={() => setComposingCategory('todo')} />,
    [],
  );
  useSetTopBarSlots({ right: topBarRight, cta: topBarCta });

  return (
    // Mirrors the prototype's `.v1-board-wrap`:
    //   flex: 1; position: relative;
    //   overflow-x: auto; overflow-y: hidden;
    //   padding: 22px 22px 0;
    //
    // The shell already sits inside the `.v1-main` flow, so the
    // outer wrap takes its full height and the inner `.v1-board` grid
    // owns the column layout.
    //
    // `-mx-6` cancels the workspace shell's `px-6` content padding
    // — the prototype's board wrap manages its own 22px horizontal
    // gutter, and stacking the two would double-pad the columns.
    <div className="relative -mx-6 -my-6 flex h-[calc(100%+3rem)] flex-1 flex-col overflow-x-auto overflow-y-hidden px-[22px] pt-[22px]">
      {/*
        Radial colour wash behind the board surface. Two large blurred
        radials (orange top-right, navy mid-left) — `.v1-board-glow`.
        aria-hidden, pointer-events-none, blurred 20px.
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 blur-[20px]"
        style={{
          background:
            'radial-gradient(60% 50% at 80% 0%, var(--brand-glow-1), transparent 70%),' +
            'radial-gradient(50% 60% at 10% 30%, var(--brand-glow-2), transparent 60%)',
        }}
      />

      {/* Side-effect: contributes breadcrumbs to the persistent topbar.
          Renders nothing in the page tree. */}
      <ProjectPageHeader
        workspaceSlug={workspaceSlug}
        workspaceName={workspaceName}
        projectSlug={projectSlug}
        projectName={projectName}
        current="board"
      />

      {tasks.isLoading || statuses.isLoading ? (
        <p className="text-foreground-muted relative z-10 text-sm">Loading board…</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveTaskId(null)}
        >
          {/*
            Prototype `.v1-board`:
              display: grid;
              grid-template-columns: repeat(3, minmax(280px, 1fr));
              gap: 18px;
              min-width: max-content;
              height: 100%;

            `min-w-max` ensures the columns retain their 280px minimum
            even when the wrap's overflow-x kicks in.
          */}
          <div className="relative z-[1] grid h-full min-w-max grid-cols-[repeat(3,minmax(280px,1fr))] gap-[18px]">
            {CATEGORY_ORDER.map((cat) => (
              <BoardSwimlane
                key={cat}
                category={cat}
                label={CATEGORY_LABEL[cat]}
                workspaceSlug={workspaceSlug}
                tasks={cardsByCategory[cat]}
                tagLookup={tagLookup}
                activeTaskId={activeTaskId}
                totalTaskCount={totalTaskCount}
                isComposing={composingCategory === cat}
                onStartComposing={() => setComposingCategory(cat)}
                onStopComposing={() =>
                  setComposingCategory((current) => (current === cat ? null : current))
                }
                onCreateTask={
                  defaultStatusByCategory[cat]
                    ? (title) => handleCreateInSwimlane(cat, title)
                    : null
                }
              />
            ))}
          </div>

          <DragOverlay dropAnimation={dropAnimation}>
            {activeCard ? (
              <BoardSwimlaneDragOverlay
                task={activeCard}
                workspaceSlug={workspaceSlug}
                tagLookup={tagLookup}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};
